import React, { useEffect, useState } from 'react';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import Pagination from '@mui/material/Pagination';
import { useMyContext } from '../../../context/MyContext';
import { FaCcVisa, FaCcMastercard, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFilter, FaTimes, FaCheckCircle } from "react-icons/fa";
import PaginationItem from '@mui/material/PaginationItem';
import { Base_Url, topUpList, userTrnxList } from '../../../config/config';

const B2bTopupHistory = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const cus_id = localStorage.getItem('cus_id');
  const [totalOrders, setTotalOrders] = useState(0);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [historyData, setHistoryData] = useState([]);
  const [allHistoryData, setAllHistoryData] = useState([]); // Store all data for client-side filtering
  const navigate = useNavigate();

  // Add date filter states
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  const handleNavigation = () => {
    navigate('b2b/dashboard');
  };

  const getTopupHistory = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=${page}&limit=${limit}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.status === 'SUCCESS') {
        const transactions = data.data;

        if (isDateFilterActive) {
          // Apply client-side filtering based on dates
          const filtered = applyDateFilter(transactions);
          setHistoryData(filtered);
          setTotalOrders(filtered.length);
        } else {
          setHistoryData(transactions);
          setTotalOrders(data.pagination.totalItems || 0);
        }

        // Store all data for filtering
        setAllHistoryData(prev => {
          // Combine with previous data to build complete dataset
          const combinedData = [...prev];

          // Add new transactions if they don't already exist
          transactions.forEach(transaction => {
            if (!combinedData.some(item => item.id === transaction.id)) {
              combinedData.push(transaction);
            }
          });

          return combinedData;
        });
      } else {
        toast.error(`Error: ${data.message}`);
      }
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    }
  };

  // Function to fetch all data for export
  const fetchAllData = async () => {
    const apiEndpoint = role === 'admin' ? userTrnxList : topUpList;
    const url = `${Base_Url}${apiEndpoint}?page=1&limit=1000`; // Large limit to get as much data as possible

    try {
      toast.loading('Preparing data...');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token: token,
          cus_id: cus_id,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      toast.dismiss();

      if (data.status === 'SUCCESS') {
        const allData = data.data;
        return isDateFilterActive ? applyDateFilter(allData) : allData;
      } else {
        toast.error(`Error: ${data.message}`);
        return [];
      }
    } catch (error) {
      toast.dismiss();
      toast.error(`Error: ${error.message}`);
      return [];
    }
  };

  useEffect(() => {
    getTopupHistory();
  }, [page, limit]);

  useEffect(() => {
    if (isDateFilterActive) {
      // Apply date filter to all data
      const filtered = applyDateFilter(allHistoryData);
      setFilteredData(filtered);

      // Update pagination with filtered data
      const paginatedData = filtered.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
      setTotalOrders(filtered.length);
    } else {
      // Reset to normal API-based pagination
      getTopupHistory();
    }
  }, [isDateFilterActive, startDate, endDate]);

  useEffect(() => {
    if (isDateFilterActive) {
      // When page or limit changes with active filter, update displayed data
      const paginatedData = filteredData.slice((page - 1) * limit, page * limit);
      setHistoryData(paginatedData);
    }
  }, [page, limit, filteredData]);

  const applyDateFilter = (data) => {
    if (!startDate || !endDate) return data;

    const startTimestamp = new Date(startDate).setHours(0, 0, 0, 0);
    const endTimestamp = new Date(endDate).setHours(23, 59, 59, 999);

    return data.filter(item => {
      const txnDate = new Date(item.txn_date).getTime();
      return txnDate >= startTimestamp && txnDate <= endTimestamp;
    });
  };

  const handleApplyDateFilter = () => {
    if (!startDate || !endDate) {
      toast.warning('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.warning('Start date cannot be after end date');
      return;
    }

    setIsDateFilterActive(true);
    setPage(1); // Reset to first page when filter changes
  };

  const handleResetDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setIsDateFilterActive(false);
    setPage(1);
  };

  // Function to convert JSON to CSV
  const convertToCSV = (data) => {
    const headers = role === 'admin'
      ? ['User ID', 'Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date']
      : ['Transaction ID', 'Amount', 'Last Balance', 'Transaction Type', 'Transaction Status', 'Transaction Date'];

    // Create CSV header row
    let csvContent = headers.join(',') + '\n';

    // Add data rows
    data.forEach(row => {
      const values = role === 'admin'
        ? [
          `"${row.user_id || 'N/A'}"`,
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ]
        : [
          `"${row.trnx_id || 'N/A'}"`,
          `"${row.credit_amount || 'N/A'}"`,
          `"${row.closing_balance || 'N/A'}"`,
          `"${row.payment_mode || 'N/A'}"`,
          `"${row.status === 'add' ? 'Success' : row.status || 'N/A'}"`,
          `"${row.txn_date || 'N/A'}"`,
        ];

      csvContent += values.join(',') + '\n';
    });

    return csvContent;
  };

  const downloadTopupHistory = async () => {
    try {
      toast.loading('Preparing download...');

      // Get all data for CSV export
      const dataToExport = await fetchAllData();

      if (dataToExport.length === 0) {
        toast.dismiss();
        toast.error('No data available to download');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(dataToExport);

      // Create downloadable file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `topup-history-${startDate || 'all'}-to-${endDate || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.dismiss();
      toast.success('Download successful');
    } catch (error) {
      toast.dismiss();
      toast.error(`Download failed: ${error.message}`);
    }
  };

  const renderTableHeaders = () => {
    const headers = [
      'Transaction ID',
      'Amount',
      'Last Balance',
      'Transaction Type',
      'Transaction Status',
      'Transaction Date',
      'Description',
    ];
    if (role === 'admin') headers.unshift('User ID');
    return headers;
  };

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value));
    setPage(1);
  };


  const renderTableRow = (row, index) => (
    <tr
      key={row.id}
      className={`transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
        dark:bg-transparent dark:text-gray-100
        hover:bg-gray-100 dark:hover:bg-indigo-500/10
      `}
    >
      {/* <td className="p-3">
        <input
          type="checkbox"
          checked={selectedRows.includes(index)}
          onChange={() => handleRowSelect(index)}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
        />
      </td> */}
      {role === 'admin' && (
        <td className="p-2 text-xs text-black dark:text-indigo-200 font-normal border-b border-gray-200 dark:border-indigo-500/15">
          {row.user_id}
        </td>
      )}
      <td className="relative group p-2 text-xs text-black dark:text-indigo-200 cursor-pointer font-normal underline decoration-gray-300 dark:decoration-indigo-500/40 hover:decoration-black dark:hover:decoration-indigo-300 transition-colors border-b border-gray-200 dark:border-indigo-500/15">
        {row.trnx_id || "N/A"}
        <div className="absolute left-0 top-full mt-1 hidden group-hover:block bg-black dark:bg-indigo-600 text-white dark:text-white text-[10px] rounded-md px-2 py-1 shadow-lg z-10 whitespace-nowrap">
          💳 View transaction details here!
          <div className="absolute -top-1 left-3 w-1.5 h-1.5 bg-black dark:bg-indigo-600 rotate-45"></div>
        </div>
      </td>
      <td className="p-2 text-xs text-black font-normal dark:text-gray-100 border-b border-gray-200 dark:border-indigo-500/15">
        ₹{row.credit_amount || "N/A"}
      </td>
      <td className="p-2 text-xs text-black dark:text-gray-100 border-b border-gray-200 dark:border-indigo-500/15">
        ₹{row.closing_balance || "N/A"}
      </td>
      <td className="p-2 text-xs text-black dark:text-gray-100 capitalize border-b border-gray-200 dark:border-indigo-500/15">
        {row.payment_mode || "N/A"}
      </td>
      <td className="p-2 border-b border-gray-200 dark:border-indigo-500/15">
        {row.status === 'add' ? (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-emerald-500 text-white dark:bg-gradient-to-r dark:from-emerald-500 dark:to-teal-500 dark:text-white dark:border-emerald-400/40 dark:shadow-[0_0_12px_rgba(16,185,129,0.4)]">
            Success
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-black border-gray-400 dark:bg-indigo-500/20 dark:text-indigo-200 dark:border-indigo-400/40">
            {row.status}
          </span>
        )}
      </td>
      <td className="p-2 text-xs text-black dark:text-gray-100 border-b border-gray-200 dark:border-indigo-500/15">
        {row.txn_date || "N/A"}
      </td>
      <td className="p-2 text-xs text-black dark:text-gray-100 max-w-xs border-b border-gray-200 dark:border-indigo-500/15">
        <div
          className="truncate hover:text-clip hover:whitespace-normal cursor-help"
          title={row.txn_description || "No description available"}
        >
          {row.txn_description || "N/A"}
        </div>
      </td>
    </tr>
  );

  // Mobile card view for each row
  const renderMobileCard = (row, index) => (
    <div
      key={row.id || index}
      className={`rounded-xl border overflow-hidden transition-all duration-200 ${
        isDarkMode
          ? 'bg-gradient-to-br from-[#0f172a] via-[#0b1220] to-[#0f172a] border-indigo-500/25 shadow-[0_4px_20px_rgba(79,70,229,0.15)] hover:border-indigo-400/40 hover:shadow-[0_6px_24px_rgba(99,102,241,0.25)]'
          : 'bg-white border-gray-200 shadow-sm'
      }`}
    >
      {/* Top strip: Transaction ID + Amount */}
      <div className={`flex items-center justify-between gap-3 px-3 py-2.5 border-b ${
        isDarkMode
          ? 'bg-gradient-to-r from-indigo-600/20 via-purple-600/15 to-indigo-600/20 border-indigo-500/25'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="min-w-0 flex-1">
          <p className={`text-[9px] uppercase tracking-wider mb-0.5 font-semibold ${
            isDarkMode ? 'text-indigo-300/80' : 'text-gray-400'
          }`}>
            Transaction ID
          </p>
          <p className={`text-[11px] font-semibold truncate ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {row.trnx_id || 'N/A'}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className={`text-[9px] uppercase tracking-wider mb-0.5 font-semibold ${
            isDarkMode ? 'text-indigo-300/80' : 'text-gray-400'
          }`}>
            Amount
          </p>
          <p className={`text-[14px] font-bold ${
            isDarkMode
              ? 'bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent'
              : 'text-gray-900'
          }`}>
            ₹{row.credit_amount || 'N/A'}
          </p>
        </div>
      </div>

      {/* Body: detail rows */}
      <div className={`px-3 py-2 divide-y ${
        isDarkMode ? 'divide-indigo-500/15' : 'divide-gray-100'
      }`}>
        {role === 'admin' && (
          <div className="flex items-center justify-between py-2">
            <span className={`text-[10px] font-medium uppercase tracking-wider ${
              isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
            }`}>
              User ID
            </span>
            <span className={`text-[11px] font-medium ${
              isDarkMode ? 'text-indigo-100' : 'text-gray-800'
            }`}>
              {row.user_id || 'N/A'}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between py-2">
          <span className={`text-[10px] font-medium uppercase tracking-wider ${
            isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
          }`}>
            Last Balance
          </span>
          <span className={`text-[11px] font-semibold ${
            isDarkMode ? 'text-cyan-200' : 'text-gray-800'
          }`}>
            ₹{row.closing_balance || 'N/A'}
          </span>
        </div>

        <div className="flex items-center justify-between py-2 gap-3">
          <span className={`text-[10px] font-medium uppercase tracking-wider shrink-0 ${
            isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
          }`}>
            Type
          </span>
          <span className={`text-[11px] font-medium capitalize ${
            isDarkMode ? 'text-slate-300' : 'text-gray-800'
          }`}>
            {row.payment_mode || 'N/A'}
          </span>
        </div>

        <div className="flex items-center justify-between py-2 gap-3">
          <span className={`text-[10px] font-medium uppercase tracking-wider shrink-0 ${
            isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
          }`}>
            Status
          </span>
          {row.status === 'add' ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold border bg-emerald-500 text-white dark:bg-gradient-to-r dark:from-emerald-500 dark:to-teal-500 dark:text-white dark:border-emerald-400/40 dark:shadow-[0_0_12px_rgba(16,185,129,0.4)]">
              Success
            </span>
          ) : (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${
              isDarkMode
                ? 'bg-indigo-500/20 text-indigo-200 border-indigo-400/40'
                : 'bg-gray-100 text-black border-gray-400'
            }`}>
              {row.status}
            </span>
          )}
        </div>

        <div className="flex items-center justify-between py-2 gap-3">
          <span className={`text-[10px] font-medium uppercase tracking-wider shrink-0 ${
            isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
          }`}>
            Date
          </span>
          <span className={`text-[11px] font-medium text-right ${
            isDarkMode ? 'text-slate-300' : 'text-gray-800'
          }`}>
            {row.txn_date || 'N/A'}
          </span>
        </div>

        <div className="py-2">
          <p className={`text-[10px] font-medium uppercase tracking-wider mb-1 ${
            isDarkMode ? 'text-indigo-300/70' : 'text-gray-500'
          }`}>
            Description
          </p>
          <p className={`text-[11px] leading-relaxed break-words ${
            isDarkMode ? 'text-slate-200' : 'text-gray-800'
          }`}>
            {row.txn_description || 'N/A'}
          </p>
        </div>
      </div>
    </div>
  );

  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";
  return (
    <div
      className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-white'
        }`}
    >
      <div className="z-10">
        <NavBar />
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div
          className={`${isToggleSidebar ? "md:w-64" : "md:w-20"} w-0 m-0 md:m-3 md:rounded-3xl ${glass} overflow-visible md:overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0`}
          // onClick={handlePageClick}
        >
          <Sidebar />
        </div>

        {/* Page wrapper: fixed, no page-level scroll */}
        <div className="flex flex-col w-full h-full overflow-hidden bg-gray-200 dark:bg-black dark:text-white border border-gray-200 dark:border-indigo-500/20 ">

          {/* Header (fixed, does not scroll) */}
          <div className="shrink-0 p-3 sm:p-4 border-b border-gray-200 dark:border-indigo-500/20">
            <button
              // onClick={handleNavigation}
              className="text-[13px] sm:text-[14px] font-bold bg-emerald-600 dark:bg-gradient-to-r dark:from-indigo-600 dark:to-purple-600 dark:hover:from-indigo-500 dark:hover:to-purple-500 dark:text-white dark:shadow-lg dark:shadow-indigo-900/50 dark:border-indigo-400/40 text-white font-sans rounded h-[40px] w-full flex items-center justify-center"
            >
              <FaCcVisa className="mr-2" size={20} />
              <FaCcMastercard className="mr-2" size={20} />
              TopUp History
            </button>

            {/* Date Picker Section */}
            <div className="p-2 mt-2 border border-gray-200 dark:border-indigo-500/20 rounded-lg bg-white dark:bg-gradient-to-br dark:from-[#0b0b14] dark:via-[#0d0d18] dark:to-[#0b0b14]">
              <h3 className="text-[11px] sm:text-[12px] font-semibold mb-1 text-black dark:text-indigo-200 uppercase tracking-wide">Filter Transactions</h3>

              <div className="flex flex-col md:flex-row items-start md:items-end gap-3 md:gap-4">
                {/* Start Date Picker */}
                <div className="flex flex-col w-full md:w-auto">
                  <label className="text-xs font-medium mb-1.5 text-black dark:text-indigo-200/80">Start Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="border border-black dark:border-indigo-500/40 rounded-lg p-2.5 pr-9 w-full md:w-48 text-xs bg-white dark:bg-black/60 dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-indigo-500/60 dark:focus:border-indigo-400 focus:border-transparent transition-all [color-scheme:dark]"
                    />
                    <FaCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-black dark:text-indigo-300 text-xs" />
                  </div>
                </div>

                {/* End Date Picker */}
                <div className="flex flex-col w-full md:w-auto">
                  <label className="text-xs font-medium mb-1.5 text-black dark:text-indigo-200/80">End Date</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="border border-black dark:border-indigo-500/40 rounded-lg p-2.5 pr-9 w-full md:w-48 text-xs bg-white dark:bg-black/60 dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-indigo-500/60 dark:focus:border-indigo-400 focus:border-transparent transition-all [color-scheme:dark]"
                    />
                    <FaCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-black dark:text-indigo-300 text-xs" />
                  </div>
                </div>

                {/* Buttons Group */}
                <div className="flex flex-wrap gap-2 w-full md:w-auto md:ml-auto">
                  <button
                    onClick={handleApplyDateFilter}
                    className="bg-blue-600 hover:bg-blue-700 dark:bg-gradient-to-r dark:from-indigo-600 dark:to-purple-600 dark:hover:from-indigo-500 dark:hover:to-purple-500 dark:text-white dark:shadow-lg dark:shadow-indigo-900/50 text-white font-medium rounded-lg px-4 py-2.5 text-xs flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0"
                  >
                    <FaFilter className="mr-2" />
                    Apply Filter
                  </button>

                  {isDateFilterActive && (
                    <button
                      onClick={handleResetDateFilter}
                      className="bg-white hover:bg-gray-100 text-black dark:bg-black dark:text-white dark:hover:bg-indigo-500/10 font-medium rounded-lg px-4 py-2.5 text-xs flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0 border border-black dark:border-indigo-500/40"
                    >
                      <FaTimes className="mr-2" />
                      Reset
                    </button>
                  )}

                  <button
                    onClick={downloadTopupHistory}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white dark:bg-black dark:text-white dark:hover:bg-indigo-500/10 font-medium rounded-lg px-4 py-2.5 text-xs flex items-center justify-center transition-colors duration-200 flex-grow md:flex-grow-0 dark:border-indigo-500/40"
                  >
                    <FaDownload className="mr-2" />
                    Download
                  </button>
                </div>
              </div>

              {/* Filter Status Message */}
              {isDateFilterActive && (
                <div className="mt-3 text-xs font-medium flex items-center text-black dark:text-indigo-200">
                  <FaCheckCircle className="mr-2 dark:text-indigo-300" />
                  Showing data from {new Date(startDate).toLocaleDateString()} to {new Date(endDate).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>

          {/* Table area: ONLY this part scrolls */}
          <div className="flex-1 overflow-auto custom-scrollbar px-2 sm:px-4">
            {/* Mobile/Tablet Card View (hidden on lg+) */}
            <div className="lg:hidden space-y-3 pt-3">
              {historyData?.length > 0 ? (
                historyData.map((row, index) => renderMobileCard(row, index))
              ) : (
                <div className={`text-center p-6 rounded-lg border ${isDarkMode ? 'border-indigo-500/20 text-indigo-300/60' : 'border-gray-200 text-gray-500'}`}>
                  {isDateFilterActive ? 'No data available for the selected date range' : 'No data available'}
                </div>
              )}
            </div>

            {/* Desktop Table View (hidden below lg) */}
            <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200 dark:border-indigo-500/20">
              <table className="min-w-full table-auto dark:bg-black dark:text-white">
                <thead className='bg-emerald-600 text-white text-[10px] dark:bg-gradient-to-r dark:from-indigo-700 dark:via-purple-700 dark:to-indigo-700 dark:text-white dark:border-b dark:border-indigo-500/40 sticky top-0 z-10'>
                  <tr>
                    {/* <th className="p-2 text-left font-medium">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 ml-1 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
                      />
                    </th> */}
                    {renderTableHeaders().map((header, index) => (
                      <th key={index} className="p-2 text-left font-medium">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-indigo-500/15 dark:bg-black dark:text-white">
                  {historyData?.length > 0 ? (
                    historyData.map((row, index) => renderTableRow(row, index))
                  ) : (
                    <tr>
                      <td colSpan={role === 'admin' ? "9" : "8"} className="text-center p-4 text-gray-500 dark:text-indigo-300/60">
                        {isDateFilterActive ? 'No data available for the selected date range' : 'No data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer / pagination (fixed, does not scroll) */}
          <div className="shrink-0 p-2 sm:p-3 border-t border-gray-200 dark:border-indigo-500/20 flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0 sm:space-y-0">
            <span className="text-xs text-black font-bold dark:text-indigo-200">{`Total : ${totalOrders}`}</span>
            <select
              id="limit"
              value={limit}
              onChange={handleLimitChange}
              className="border border-black dark:border-indigo-500/40 rounded p-1 text-xs bg-white dark:bg-black dark:text-white"
            >
              {totalOrders === 0 ? (
                <option value={0}>0</option>
              ) : (
                <>
                  {totalOrders >= 10 && <option value={10}>10</option>}
                  {totalOrders >= 15 && <option value={15}>15</option>}
                  {totalOrders >= 20 && <option value={20}>20</option>}
                  {totalOrders >= 30 && <option value={30}>30</option>}
                  {totalOrders >= 30 && <option value={totalOrders}>All</option>}
                </>
              )}
            </select>

            <Pagination
              count={Math.ceil(totalOrders / limit)}
              page={page}
              onChange={handlePaginationChange}
              variant="outlined"
              shape="rounded"
              className="pagination"
              size="small"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  className={`mx-1 rounded-md ${item.selected ? 'bg-black text-white dark:bg-white dark:text-black' : 'text-black dark:text-gray-300'} hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black`}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
export default B2bTopupHistory;